/**
 * screens/settings/privacy.tsx — Data & Privacy (P-009 export / P-010 erasure),
 * ADMIN-only. Faithful RN port of design/security.jsx PrivacyPage + ExportPanel
 * + ErasePanel + ContactPicker. DPDP portability (JSON export jobs) and the
 * type-ERASE erasure flow with 30-day SLA tracking.
 */

import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { useResponsive } from '../../utils/responsive';
import { SettingsHeader, Panel, StatusBadge } from '../../components/platform/primitives';
import { PButton, PModal, PTextInput, PEmpty } from '../../components/platform/controls';
import Tabs from '../../components/tabs';
import Alert from '../../components/alert';
import Avatar from '../../components/avatar';
import Icon from '../../components/icon';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, R } from '../../utils/platformTokens';

type Contact = { id: string; name: string; email: string; phone: string };

const ContactPicker: React.FC<{ value: Contact | null; onChange: (c: Contact) => void; label: string }> = ({ value, onChange, label }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state } = usePlatform();
  const [q, setQ] = useState('');
  const matches = state.contacts.filter((x) => `${x.name} ${x.email} ${x.id}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <View>
      <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, color: c.ink2, marginBottom: 6 }}>{label}</Text>
      <PTextInput leadingIcon="search" placeholder="Search by name, email or contact ID…" value={q} onChangeText={setQ} />
      <View style={{ marginTop: 8, borderWidth: 1, borderColor: c.line, borderRadius: R.r3, overflow: 'hidden' }}>
        {matches.map((x, i) => (
          <Pressable
            key={x.id}
            onPress={() => onChange(x)}
            style={{
              flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10,
              borderTopWidth: i ? 1 : 0, borderColor: c.line,
              backgroundColor: value?.id === x.id ? c.accentSoft : c.card,
            }}
          >
            <Avatar name={x.name} size="sm" />
            <View style={{ flex: 1 }}>
              <Text style={{ fontWeight: FW.medium, fontSize: FS.md, color: c.ink }}>{x.name}</Text>
              <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{x.email} · {x.id}</Text>
            </View>
            {value?.id === x.id && <Icon name="check" size={16} color={c.accent} />}
          </Pressable>
        ))}
        {matches.length === 0 && <Text style={{ padding: 14, color: c.ink3, fontSize: FS.sm }}>No contacts found.</Text>}
      </View>
    </View>
  );
};

const ExportPanel: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const { width } = useResponsive();
  const [sel, setSel] = useState<Contact | null>(null);
  const [jobs, setJobs] = useState([{ id: 'exp_4a91', contact: 'Lakshmi Rao', cid: 'C-1033', status: 'ready', expiresInDays: 5 }]);
  const twoCol = width > 720;

  const start = () => {
    if (!sel) return;
    const job = { id: 'exp_' + Math.random().toString(36).slice(2, 6), contact: sel.name, cid: sel.id, status: 'queued', expiresInDays: 7 };
    setJobs((j) => [job, ...j]);
    toast.success(`Export started for ${sel.name}. We'll notify you when it's ready.`);
    setSel(null);
    setTimeout(() => setJobs((j) => j.map((x) => (x.id === job.id ? { ...x, status: 'processing' } : x))), 1200);
    setTimeout(() => setJobs((j) => j.map((x) => (x.id === job.id ? { ...x, status: 'ready' } : x))), 3000);
  };

  return (
    <View style={{ flexDirection: twoCol ? 'row' : 'column', gap: 16 }}>
      <View style={{ flex: 1 }}>
        <Panel title="Start a data export" style={{ marginBottom: 0 }}>
          <Text style={{ color: c.ink2, fontSize: FS.sm, marginBottom: 14 }}>
            Gathers a contact's CRM profile, bookings, call records and voice history across the mesh into a structured JSON file. This is data portability — nothing is deleted.
          </Text>
          <ContactPicker value={sel} onChange={setSel} label="Contact" />
          <View style={{ marginTop: 14 }}><PButton kind="accent" icon="download" disabled={!sel} onPress={start}>Start export</PButton></View>
        </Panel>
      </View>
      <View style={{ flex: 1 }}>
        <Panel title="Export jobs" style={{ marginBottom: 0 }}>
          {jobs.length === 0 ? <PEmpty icon="download" title="No exports yet">Start an export to see its status here.</PEmpty> : (
            <View style={{ gap: 10 }}>
              {jobs.map((j) => (
                <View key={j.id} style={{ borderWidth: 1, borderColor: c.line, borderRadius: R.r3, padding: 12 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <View>
                      <Text style={{ fontWeight: FW.medium, color: c.ink }}>{j.contact} <Text style={{ fontFamily: theme.font.mono, color: c.ink3, fontSize: FS.sm }}>{j.cid}</Text></Text>
                      <Text style={{ fontFamily: theme.font.mono, fontSize: FS.xs, color: c.ink4 }}>{j.id}</Text>
                    </View>
                    <StatusBadge status={j.status} label={{ queued: 'Queued', processing: 'Processing', ready: 'Ready' }[j.status as 'queued']} />
                  </View>
                  {j.status === 'ready' && (
                    <View style={{ marginTop: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Text style={{ fontSize: FS.xs, color: c.warn }}>Secure link expires in {j.expiresInDays}d</Text>
                      <PButton size="sm" icon="download" onPress={() => toast.success('Downloading JSON payload…')}>Download JSON</PButton>
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}
        </Panel>
      </View>
    </View>
  );
};

const ErasePanel: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const { width } = useResponsive();
  const [sel, setSel] = useState<Contact | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [token, setToken] = useState('');
  const [jobs, setJobs] = useState<{ id: string; contact: string; cid: string; completesBy: string }[]>([]);
  const TOKEN = 'ERASE';
  const twoCol = width > 720;
  const alreadyInProgress = sel && jobs.some((j) => j.cid === sel.id);

  const trigger = () => {
    if (!sel) return;
    const job = { id: 'ers_' + Math.random().toString(36).slice(2, 6), contact: sel.name, cid: sel.id, completesBy: '07 Jul 2026' };
    setJobs((j) => [job, ...j]);
    toast.warn(`Erasure started for ${sel.name}. Completes within 30 days.`);
    setConfirmOpen(false); setToken(''); setSel(null);
  };

  return (
    <View style={{ flexDirection: twoCol ? 'row' : 'column', gap: 16 }}>
      <View style={{ flex: 1 }}>
        <Panel title="Erase a contact's data" style={{ marginBottom: 0 }}>
          <Alert tone="danger" title="This cannot be undone">Erasure permanently removes the contact's files, logs, appointments and recordings across the mesh.</Alert>
          <View style={{ marginTop: 14 }}><ContactPicker value={sel} onChange={setSel} label="Contact to erase" /></View>
          {alreadyInProgress ? (
            <View style={{ marginTop: 14 }}><Alert tone="warn">An erasure for this contact is already in progress — see its status on the right.</Alert></View>
          ) : (
            <View style={{ marginTop: 14 }}><PButton kind="danger" icon="trash" disabled={!sel} onPress={() => setConfirmOpen(true)}>Erase contact data</PButton></View>
          )}
        </Panel>
      </View>
      <View style={{ flex: 1 }}>
        <Panel title="Erasure jobs" style={{ marginBottom: 0 }}>
          {jobs.length === 0 ? <PEmpty icon="trash" title="No erasures yet">Erasure requests and their 30-day SLA status appear here.</PEmpty> : (
            <View style={{ gap: 10 }}>
              {jobs.map((j) => (
                <View key={j.id} style={{ borderWidth: 1, borderColor: c.dangerLine, backgroundColor: c.dangerSoft, borderRadius: R.r3, padding: 12 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <View>
                      <Text style={{ fontWeight: FW.medium, color: c.ink }}>{j.contact} <Text style={{ fontFamily: theme.font.mono, color: c.ink3, fontSize: FS.sm }}>{j.cid}</Text></Text>
                      <Text style={{ fontFamily: theme.font.mono, fontSize: FS.xs, color: c.ink4 }}>{j.id}</Text>
                    </View>
                    <StatusBadge status="processing" label="In progress" />
                  </View>
                  <Text style={{ marginTop: 8, fontSize: FS.sm, color: c.danger }}>Deletion in progress — completes by {j.completesBy} (30-day SLA).</Text>
                </View>
              ))}
            </View>
          )}
        </Panel>
      </View>

      <PModal
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setToken(''); }}
        title={`Erase ${sel?.name}'s data?`}
        footer={<>
          <PButton kind="ghost" onPress={() => { setConfirmOpen(false); setToken(''); }}>Cancel</PButton>
          <PButton kind="danger" disabled={token !== TOKEN} onPress={trigger}>Permanently erase</PButton>
        </>}
      >
        <Alert tone="danger" title="Cascading & irreversible">
          The following will be permanently deleted for {sel?.name} ({sel?.id}):
        </Alert>
        <View style={{ marginVertical: 12, paddingLeft: 8, gap: 6 }}>
          {['CRM profile & custom fields', 'Contact logs & notes', 'Appointments & bookings', 'Call & voice recordings'].map((t) => (
            <Text key={t} style={{ color: c.ink2, fontSize: FS.md }}>•  {t}</Text>
          ))}
        </View>
        <PTextInput label={`Type ${TOKEN} to confirm`} value={token} onChangeText={setToken} placeholder={TOKEN} mono />
      </PModal>
    </View>
  );
};

const PrivacyScreen: React.FC = () => {
  const { role } = usePlatform();
  const [tab, setTab] = useState('export');
  if (role !== 'ADMIN') return <PermissionDenied />;
  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Data & Privacy" sub="Fulfil DPDP data-portability and erasure requests for individual contacts." />
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'export', label: 'Export (portability)' }, { id: 'erase', label: 'Erase (right to be forgotten)' }]} />
      <View style={{ height: 16 }} />
      {tab === 'export' ? <ExportPanel /> : <ErasePanel />}
    </ScrollView>
  );
};

export default PrivacyScreen;
